import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { DocumentRenderData, RequiredField } from '../types'

export const w9RequiredFields: RequiredField[] = [
  { key: 'exchanger.names', description: 'Exchanger full name', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.taxIdNumber', description: 'Exchanger Tax ID / SSN', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.street', description: 'Exchanger street address', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.city', description: 'Exchanger city', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.state', description: 'Exchanger state', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.zipCode', description: 'Exchanger ZIP code', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
]

interface Props {
  data: DocumentRenderData
}

export function W9Template({ data }: Props) {
  const { exchanger, signature } = data
  const [showSSN, setShowSSN] = useState(false)
  return (
    <div className="space-y-3 text-sm text-foreground bg-card p-6 border-2 border-foreground/80">
      <div className="flex justify-between items-start border-b-2 border-foreground/80 pb-2">
        <div>
          <h4 className="text-2xl font-bold text-foreground">Form W-9</h4>
          <p className="text-xs text-muted-foreground">(Rev. October 2018)</p>
          <p className="text-xs text-muted-foreground mt-1">Department of the Treasury<br/>Internal Revenue Service</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold">Request for Taxpayer</p>
          <p className="text-sm font-bold">Identification Number and Certification</p>
          <p className="text-xs mt-1">▶ Go to <span className="underline">www.irs.gov/FormW9</span> for instructions and the latest information.</p>
        </div>
      </div>

      <div className="space-y-3 mt-4">
        <div className="border border-foreground/80 p-2">
          <label className="text-xs font-semibold text-muted-foreground">
            1  Name (as shown on your income tax return). Name is required on this line; do not leave this line blank.
          </label>
          <p className="font-medium mt-1 text-base border-b border-border pb-1">{exchanger.names}</p>
        </div>

        <div className="border border-foreground/80 p-2">
          <label className="text-xs font-semibold text-muted-foreground">
            2  Business name/disregarded entity name, if different from above
          </label>
          <p className="text-muted-foreground italic mt-1 border-b border-border pb-1">(blank)</p>
        </div>

        <div className="border border-foreground/80 p-2">
          <label className="text-xs font-semibold text-muted-foreground mb-2 block">
            3  Check appropriate box for federal tax classification of the person whose name is entered on line 1. Check only one of the following seven boxes.
          </label>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              { value: 'individual', label: 'Individual/sole proprietor or single-member LLC' },
              { value: 'c-corp', label: 'C Corporation' },
              { value: 's-corp', label: 'S Corporation' },
              { value: 'partnership', label: 'Partnership' },
              { value: 'trust-estate', label: 'Trust/estate' },
            ].map(({ value, label }) => {
              const checked = (exchanger.taxClassification || 'individual') === value
              return (
                <div key={value} className="flex items-center space-x-2">
                  <div className={`w-4 h-4 border-2 flex items-center justify-center ${checked ? 'border-foreground bg-foreground' : 'border-foreground/80'}`}>
                    {checked && <span className="text-background font-bold">✓</span>}
                  </div>
                  <span>{label}</span>
                </div>
              )
            })}
            <div className="flex items-center space-x-2">
              {(() => {
                const checked = exchanger.taxClassification === 'llc'
                return (
                  <div className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'border-foreground bg-foreground' : 'border-foreground/80'}`}>
                    {checked && <span className="text-background font-bold">✓</span>}
                  </div>
                )
              })()}
              <span>
                Limited liability company. Enter the tax classification (C=C corporation, S=S corporation, P=Partnership) ▶
                {exchanger.taxClassification === 'llc' && exchanger.llcTaxClassification && (
                  <strong className="ml-1">{exchanger.llcTaxClassification}</strong>
                )}
              </span>
            </div>
          </div>
          <p className="text-xs mt-2 text-muted-foreground italic">
            Note: Check the appropriate box in the line above for the tax classification of the single-member owner.
            Do not check LLC if the LLC is classified as a single-member LLC that is disregarded from the owner unless
            the owner of the LLC is another LLC that is not disregarded from the owner for U.S. federal tax purposes.
            Otherwise, a single-member LLC that is disregarded from the owner should check the appropriate box for the
            tax classification of its owner.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="border border-foreground/80 p-2">
            <label className="text-xs font-semibold text-muted-foreground">
              4  Exemptions (codes apply only to certain entities, not individuals; see instructions on page 3):
            </label>
            <div className="mt-1">
              <p className="text-xs">Exempt payee code (if any) <span className="border-b border-border inline-block w-16 ml-1">&nbsp;</span></p>
            </div>
          </div>
          <div className="border border-foreground/80 p-2">
            <label className="text-xs font-semibold text-muted-foreground">&nbsp;</label>
            <div className="mt-1">
              <p className="text-xs">Exemption from FATCA reporting code (if any) <span className="border-b border-border inline-block w-16 ml-1">&nbsp;</span></p>
            </div>
          </div>
        </div>

        <div className="border border-foreground/80 p-2">
          <label className="text-xs font-semibold text-muted-foreground">5  Address (number, street, and apt. or suite no.) See instructions.</label>
          <p className="font-medium mt-1 border-b border-border pb-1">
            {exchanger.street || exchanger.homeAddress || ''}
          </p>
        </div>

        <div className="border border-foreground/80 p-2">
          <label className="text-xs font-semibold text-muted-foreground">6  City, state, and ZIP code</label>
          <p className="font-medium mt-1 border-b border-border pb-1">
            {exchanger.city && exchanger.state && exchanger.zipCode
              ? `${exchanger.city}, ${exchanger.state} ${exchanger.zipCode}`
              : ''}
          </p>
        </div>

        <div className="border border-foreground/80 p-2">
          <label className="text-xs font-semibold text-muted-foreground">7  List account number(s) here (optional)</label>
          <p className="mt-1 border-b border-border pb-1">&nbsp;</p>
        </div>

        <div className="border-2 border-foreground/80 p-3 bg-muted/50">
          <p className="text-xs font-bold mb-2">Part I      Taxpayer Identification Number (TIN)</p>
          <p className="text-xs mb-2">
            Enter your TIN in the appropriate box. The TIN provided must match the name given on line 1 to avoid backup
            withholding. For individuals, this is generally your social security number (SSN). However, for a resident
            alien, sole proprietor, or disregarded entity, see the instructions for Part I, later. For other entities,
            it is your employer identification number (EIN). If you do not have a number, see How to get a TIN, later.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold">Social security number</p>
                <button
                  type="button"
                  onClick={() => setShowSSN(!showSSN)}
                  className="group flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 hover:bg-primary-200 dark:bg-primary-800 dark:hover:bg-primary-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-primary-300 dark:border-primary-600"
                  title={showSSN ? 'Hide SSN' : 'Show SSN'}
                >
                  {showSSN ? (
                    <>
                      <EyeOff className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                      <span className="text-xs font-medium text-primary-700 dark:text-primary-300">Hide</span>
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                      <span className="text-xs font-medium text-primary-700 dark:text-primary-300">Show</span>
                    </>
                  )}
                </button>
              </div>
              <div className="flex items-center justify-center space-x-1 font-mono text-lg bg-card border-2 border-foreground/80 p-2">
                {showSSN ? (
                  <>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(0) || '•'}</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(1) || '•'}</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(2) || '•'}</span>
                    <span className="font-bold">-</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(4) || '•'}</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(5) || '•'}</span>
                    <span className="font-bold">-</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(7) || '•'}</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(8) || '•'}</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(9) || '•'}</span>
                    <span className="px-2 border border-border">{exchanger.taxIdNumber.charAt(10) || '•'}</span>
                  </>
                ) : (
                  <>
                    <span className="px-2 border border-border">•</span>
                    <span className="px-2 border border-border">•</span>
                    <span className="px-2 border border-border">•</span>
                    <span className="font-bold">-</span>
                    <span className="px-2 border border-border">•</span>
                    <span className="px-2 border border-border">•</span>
                    <span className="font-bold">-</span>
                    <span className="px-2 border border-border">•</span>
                    <span className="px-2 border border-border">•</span>
                    <span className="px-2 border border-border">•</span>
                    <span className="px-2 border border-border">•</span>
                  </>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold mb-1">or</p>
              <p className="text-xs font-semibold mb-1">Employer identification number</p>
              <div className="flex items-center justify-center space-x-1 font-mono text-lg bg-card border-2 border-foreground/80 p-2">
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="font-bold">-</span>
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="px-2 border border-border">&nbsp;</span>
                <span className="px-2 border border-border">&nbsp;</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 border-foreground/80 p-3 bg-muted/50">
          <p className="text-xs font-bold mb-2">Part II     Certification</p>
          <p className="text-xs leading-relaxed">Under penalties of perjury, I certify that:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2 text-xs mt-2">
            <li>
              The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); and
            </li>
            <li>
              I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified
              by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all
              interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding; and
            </li>
            <li>I am a U.S. citizen or other U.S. person (defined below); and</li>
            <li>The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.</li>
          </ol>
          <p className="text-xs mt-3 font-semibold">
            Certification instructions. You must cross out item 2 above if you have been notified by the IRS that you are
            currently subject to backup withholding because you have failed to report all interest and dividends on your tax return.
          </p>
        </div>

        <div className="border border-foreground/80 p-3 bg-blue-50 dark:bg-blue-900/20">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-foreground/80 block mb-2">Sign Here ▶</label>
              {signature?.captured ? (
                <div className="mt-2 p-3 bg-card border-2 border-blue-500 dark:border-blue-600 rounded min-h-[60px] flex items-center justify-center">
                  {signature.type === 'type' ? (
                    <p className="text-2xl font-signature text-blue-900 dark:text-blue-100" style={{ fontFamily: 'cursive' }}>
                      {signature.value}
                    </p>
                  ) : (
                    signature.value && (
                      <img
                        src={signature.value}
                        alt="Signature"
                        className="max-h-[50px] object-contain"
                      />
                    )
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">Signature will be captured below</p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground/80 block mb-2">Date ▶</label>
              <p className="font-medium">
                {signature?.capturedAt
                  ? new Date(signature.capturedAt).toLocaleDateString()
                  : new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
